/**
 * WaypointManager — Renders and manages waypoint markers on the map
 *
 * Displays waypoints as amber/gold CSS-styled markers with popups showing
 * editable labels, MGRS coordinates, lat/lng, and a delete action.
 */

import React from 'react'
import { Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import { latLngToMgrs, formatMgrsDisplay } from '../utils/mgrsConvert.js'

/**
 * Custom Leaflet divIcon for waypoint markers.
 * Amber/gold dot — pure CSS, no image files.
 */
export const waypointIcon = L.divIcon({
  className: 'waypoint-marker',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -10],
})

/**
 * WaypointManager component.
 *
 * @param {Object} props
 * @param {Array<{id: string, lat: number, lng: number, label: string, mgrs: string}>} props.waypoints
 * @param {function} props.onAdd - Called with {lat, lng} when adding a waypoint
 * @param {function} props.onRemove - Called with waypoint id to remove
 * @param {function} props.onUpdate - Called with (id, updates) to update a waypoint
 * @param {boolean} props.addMode - Whether "add waypoint" mode is active
 */
export default function WaypointManager({ waypoints, onRemove, onUpdate }) {
  if (!waypoints || waypoints.length === 0) {
    return null
  }

  return (
    <>
      {waypoints.map((wp) => {
        let mgrsDisplay
        try {
          const raw = latLngToMgrs(wp.lat, wp.lng, 3)
          mgrsDisplay = formatMgrsDisplay(raw)
        } catch {
          mgrsDisplay = '---'
        }

        return (
          <Marker key={wp.id} position={[wp.lat, wp.lng]} icon={waypointIcon}>
            <Popup>
              <div className="waypoint-popup">
                <div className="waypoint-popup-label">
                  <input
                    type="text"
                    value={wp.label}
                    onChange={(e) =>
                      onUpdate(wp.id, { label: e.target.value })
                    }
                    className="waypoint-label-input"
                    placeholder="Waypoint label"
                  />
                </div>
                <div className="waypoint-popup-coords">
                  <div className="waypoint-popup-row">
                    <span className="coord-label">MGRS</span>
                    <span className="mgrs-text">{mgrsDisplay}</span>
                  </div>
                  <div className="waypoint-popup-row">
                    <span className="coord-label">Lat/Lng</span>
                    <span className="coord-value">
                      {wp.lat.toFixed(5)}, {wp.lng.toFixed(5)}
                    </span>
                  </div>
                </div>
                <button
                  className="btn-military waypoint-delete-btn"
                  onClick={() => onRemove(wp.id)}
                  type="button"
                >
                  Delete
                </button>
              </div>
            </Popup>
          </Marker>
        )
      })}
    </>
  )
}
